// Public (unauthenticated) blog CMS reads. Safe to call from public route
// loaders — no bearer token required, RLS applies as anon.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { PublicPost } from "@/lib/blog/types";

export const listCmsPosts = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicPost[]> => {
    const { createPublicSupabase } = await import("@/lib/blog/public-client.server");
    const { listPublishedPosts } = await import("@/lib/blog/cms.server");
    try {
      return await listPublishedPosts(createPublicSupabase());
    } catch {
      // The blog index must never blank out because the CMS query failed.
      return [];
    }
  },
);

export const getCmsPost = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<PublicPost | null> => {
    const { createPublicSupabase } = await import("@/lib/blog/public-client.server");
    const { getPublishedPost } = await import("@/lib/blog/cms.server");
    return getPublishedPost(createPublicSupabase(), data.slug);
  });

/**
 * Public, unauthenticated preview of an unpublished article. The random token
 * is the only credential; it never appears on the site, in the sitemap or in
 * the AI feed, and published articles are refused so this can't become a
 * second indexable URL for live content.
 */
export const getBlogPreview = createServerFn({ method: "GET" })
  .inputValidator((d: { token: string }) =>
    z.object({ token: z.string().uuid() }).parse(d),
  )
  .handler(
    async ({
      data,
    }): Promise<{ post: PublicPost; status: string; slug: string } | null> => {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { getPostByPreviewToken } = await import("@/lib/blog/cms.server");
      return getPostByPreviewToken(supabaseAdmin, data.token);
    },
  );
